import React, { useMemo } from 'react'
import { getTime } from 'date-fns'
import { omitBy, isUndefined } from '@golden/utils'
import { ActivityType, ActivityNewYearDrawStatusType, ActivityNewYearStatusType } from '@golden/gdk-admin'
import Grid from '@material-ui/core/Grid'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import FormField from '../../default/form/FormField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import { FormState, ValueGetter } from '../../../utils/default/FormHook'
import { ActivityFormType, ActivityRequest } from './ActivityRecordDetailForm'
import useT from '../../../i18ns/admin/useT'
import { getValueFromChangeEvent, convertEmptyToUndefined, acceptUndefined, pipe, guaranteeBeKey } from '../../../utils/default/FormHelper'
import { SearchToRequestFunc } from '../../../utils/default/ComplexFlowHook'
import { newYearDrawStatusName } from '../../../constants/default/activityStatusName'

export interface ActivityNewYearRedEnvelopeFormType {
  account: string
  agent_account: string
  newYearRedEnvelopeStatus: ActivityNewYearStatusType | '--'
  serial_number: string
}

export const initialForm = (): ActivityNewYearRedEnvelopeFormType => ({
  account: '',
  agent_account: '',
  newYearRedEnvelopeStatus: '--',
  serial_number: ''
})

export const getValueFromEvent: ValueGetter<ActivityNewYearRedEnvelopeFormType> = {
  account: getValueFromChangeEvent,
  agent_account: getValueFromChangeEvent,
  newYearRedEnvelopeStatus: getValueFromChangeEvent,
  serial_number: getValueFromChangeEvent
}

export const formToRequest = (form: ActivityFormType): ActivityRequest => {
  const converted = {
    activity: ActivityType.NEW_YEAR_2026_RED_ENVELOPE,
    account: convertEmptyToUndefined(form.account),
    agent_account: convertEmptyToUndefined(form.agent_account),
    serial_number: convertEmptyToUndefined(form.serial_number),
    status: form.newYearRedEnvelopeStatus === '--' ? undefined : form.newYearRedEnvelopeStatus,
    type: form.type,
    start_at: form.time.start === null ? undefined : getTime(form.time.start),
    end_at: form.time.end === null ? undefined : getTime(form.time.end),
    page: 1
  } as ActivityRequest
  return omitBy(converted, isUndefined) as ActivityRequest
}

export const searchToRequest: SearchToRequestFunc<{
  account: string | undefined
  agent_account: string | undefined
  status: string | undefined
  serial_number: string | undefined
}> = (search) => {
  const converted = {
    account: search.account,
    agent_account: search.agent_account,
    newYearRedEnvelopeStatus: acceptUndefined(search.status, pipe(
      (value) => guaranteeBeKey(value, Object.keys(newYearDrawStatusName))
    )),
    serial_number: search.serial_number
  }
  return omitBy(converted, isUndefined) as any
}

interface PropTypes {
  context: React.Context<FormState<ActivityFormType>>
}

const TextField = React.memo(MuiTextField)

const ActivityRecordNewYearRedEnvelopeForm: React.FC<PropTypes> = (props) => {
  const { context } = props
  const { t } = useT()

  const stateOptions = useMemo(() => {
    return [{ name: t('common.all'), value: '--' }]
      .concat(
        Object.keys(newYearDrawStatusName)
          .map((key) => ({ name: t(newYearDrawStatusName[key as ActivityNewYearDrawStatusType]), value: key }))
      )
  }, [t])

  return (
    <React.Fragment>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="account"
          label={t('common.playerAccount')}
          placeholder={t('placeholder.inputPlayerAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="agent_account"
          label={t('common.agentAccount')}
          placeholder={t('placeholder.inputAgentAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, DropDownProps>
          context={context}
          component={DropDown}
          name="newYearRedEnvelopeStatus"
          label={t('common.status')}
          options={stateOptions}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="serial_number"
          label={t('common.activitySerial')}
          placeholder={t('placeholder.inputActivitySerial')}
          fullWidth
        />
      </Grid>
    </React.Fragment>
  )
}

export default React.memo(ActivityRecordNewYearRedEnvelopeForm)
